import { push } from 'react-router-redux';
import { all, call, fork, put, takeLatest, select } from 'redux-saga/effects';
import { hideSendNoticeModal, showErrorNotification, showSuccessNotification } from 'actions';
import {
  SEND_NOTICE,
  GET_NOTICES,
  GET_UNREAD_NOTICES,
  GET_NOTICE_BY_ID,
  GET_NOTICE_DELIVERY_STATUS,
  SEND_NOTICE_RESPONSE,
  GET_NOTICES_HISTORY,
  GET_NOTICE_HISTORY_BY_ID,
  GET_NOTICE_HISTORY_DELIVERY_STATUS,
  CREATE_NOTIFICATION_WITH_FILTERS,
  GET_NOTIFICATION_TARGETS,
  GET_NOTIFICATION_FILTER_OPTIONS,
  GET_NOTIFICATION_RECEIVERS,
} from 'constants/ActionTypes';
import {
  showNoticeLoader,
  hideNoticeLoader,
  showNoticeDetailsLoader,
  hideNoticeDetailsLoader,
  getNoticesSuccess,
  getNoticesNewListSuccess,
  getUnreadNoticesSuccess,
  getNoticeByIdSuccess,
  getNoticeDeliveryStatusSuccess,
  getUnreadNotices,
  sendNoticeSuccess,
  sendNoticeFailure,
  sendNoticeResponseSuccess,
  createNotificationWithFiltersSuccess,
  createNotificationWithFiltersFailure,
  getNotificationTargetsSuccess,
  getNotificationTargetsFailure,
  getNotificationFilterOptionsSuccess,
  getNotificationFilterOptionsFailure,
  getNotificationReceiversSuccess,
  getNotificationReceiversFailure,
} from 'actions/Notices';

import * as noticesApi from '../api/Notices';

const storedNoticesSelector = state => state.notices.notices;

function* createNotificationWithFiltersSaga({ payload }) {
  yield put(showNoticeLoader());

  try {
    yield call(noticesApi.createNotificationWithFilters, payload.notificationRequest);
    yield put(createNotificationWithFiltersSuccess());
    yield put(showSuccessNotification('Notification created successfully'));
    if (payload.redirectToSentOnSuccess) {
      yield put(push('/notices/all/sent'));
    }
    yield put(hideSendNoticeModal());
  } catch (error) {
    yield put(createNotificationWithFiltersFailure());
    yield put(showErrorNotification(error.messages));
  } finally {
    yield put(hideNoticeLoader());
  }
}

function* getNotificationTargetsSaga({ payload }) {
  try {
    const response = yield call(
      noticesApi.getNotificationTargets,
      payload.notificationFiltersRequest,
    );
    yield put(getNotificationTargetsSuccess(response.data));
  } catch (error) {
    yield put(getNotificationTargetsFailure());
    yield put(showErrorNotification(error.messages));
  }
}

function* getNotificationFilterOptionsSaga({ payload }) {
  try {
    const response = yield call(
      noticesApi.getNotificationFilterOptions,
      payload.notificationFiltersRequest,
    );
    yield put(getNotificationFilterOptionsSuccess(response.data));
  } catch (error) {
    yield put(getNotificationFilterOptionsFailure());
    yield put(showErrorNotification(error.messages));
  }
}

function* getNotificationReceiversSaga() {
  try {
    const response = yield call(noticesApi.getNotificationReceivers);
    yield put(getNotificationReceiversSuccess(response.data));
  } catch (error) {
    yield put(getNotificationReceiversFailure());
    yield put(showErrorNotification(error.messages));
  }
}

function* getNoticeDeliveryStatus({ payload }) {
  yield put(showNoticeDetailsLoader());

  try {
    const response = yield call(noticesApi.getNotificationDeliveryStatus, payload.id);
    yield put(getNoticeDeliveryStatusSuccess(response.data.responses));
  } catch (error) {
    yield put(showErrorNotification('errors.receiveNotificationStatus'));
  } finally {
    yield put(hideNoticeDetailsLoader());
  }
}

function* getNoticeHistoryDeliveryStatus({ payload }) {
  yield put(showNoticeDetailsLoader());

  try {
    const response = yield call(noticesApi.getNotificationHistoryDeliveryStatus, payload.id);
    yield put(
      getNoticeDeliveryStatusSuccess(
        response.data.responses ? response.data.responses : response.data,
      ),
    );
  } catch (error) {
    yield put(showErrorNotification('errors.receiveNotificationStatus'));
  } finally {
    yield put(hideNoticeDetailsLoader());
  }
}

function* getNoticeById({ payload }) {
  yield put(showNoticeDetailsLoader());

  try {
    const response = yield call(noticesApi.getNotificationById, payload.id);
    yield put(getNoticeByIdSuccess(response.data));
    yield put(getUnreadNotices());

    const storedNotices = yield select(storedNoticesSelector);

    if (storedNotices && storedNotices.list && storedNotices.list.length) {
      let shouldUpdate;
      const mappedNotices = storedNotices.list.map(notice => {
        if (notice.id === response.data.id && !notice.openedDate) {
          shouldUpdate = true;

          return {
            ...notice,
            openedDate: response.data.openedDate,
          };
        }

        return notice;
      });

      if (shouldUpdate) {
        yield put(
          getNoticesNewListSuccess({
            entities: mappedNotices,
            pageCount: storedNotices.pageCount,
          }),
        );
      }
    }
  } catch (error) {
    yield put(showErrorNotification('errors.receiveNotification'));
  } finally {
    yield put(hideNoticeDetailsLoader());
  }
}

function* getNoticeHistoryById({ payload }) {
  yield put(showNoticeDetailsLoader());

  try {
    const response = yield call(noticesApi.getNotificationHistoryById, payload.id);
    yield put(getNoticeByIdSuccess(response.data));
  } catch (error) {
    yield put(showErrorNotification('errors.receiveNotification'));
  } finally {
    yield put(hideNoticeDetailsLoader());
  }
}

function* getUnreadNoticesCount() {
  try {
    const response = yield call(noticesApi.getUnreadNotifications);
    yield put(getUnreadNoticesSuccess(response.data.count));
  } catch (error) {
    // eslint-disable-next-line
    console.log(error);
  }
}

function* getNotices({ payload }) {
  yield put(showNoticeLoader());

  try {
    const response = yield call(noticesApi.getNotifications, payload);
    yield put(getNoticesSuccess(response.data));
  } catch (error) {
    yield put(showErrorNotification('errors.receiveNotifications'));
  } finally {
    yield put(hideNoticeLoader());
  }
}

function* getNoticesHistoryMore({ payload }) {
  yield put(showNoticeLoader());
  try {
    const response = yield call(noticesApi.getNotificationsHistory, payload);
    if (payload.pagination.pageNumber === 1) {
      yield put(getNoticesNewListSuccess(response.data));
    } else {
      yield put(getNoticesSuccess(response.data));
    }
  } catch (error) {
    yield put(showErrorNotification('errors.receiveNotifications'));
  } finally {
    yield put(hideNoticeLoader());
  }
}

function* sendNotice({ payload }) {
  yield put(showNoticeLoader());

  try {
    yield call(noticesApi.sendNotification, payload.notification);
    yield put(showSuccessNotification('success.sendNotification'));
    yield put(sendNoticeSuccess());
    yield put(hideSendNoticeModal());
  } catch (error) {
    yield put(sendNoticeFailure());
    yield put(showErrorNotification('errors.sendNotification'));
  } finally {
    yield put(hideNoticeLoader());
  }
}

function* sentNoticeResponse({ payload }) {
  yield put(showNoticeDetailsLoader());

  try {
    yield call(noticesApi.sendNotificationResponse, payload.notificationId, payload.response);
    yield put(sendNoticeResponseSuccess(payload.response));
  } catch (error) {
    yield put(showErrorNotification(error.messages));
  }

  yield put(hideNoticeDetailsLoader());
}

export function* getNoticeDeliveryStatusSaga() {
  yield takeLatest(GET_NOTICE_DELIVERY_STATUS, getNoticeDeliveryStatus);
}

export function* getNoticeByIdSaga() {
  yield takeLatest(GET_NOTICE_BY_ID, getNoticeById);
}

export function* getUnreadNoticesCountSaga() {
  yield takeLatest(GET_UNREAD_NOTICES, getUnreadNoticesCount);
}

export function* getNoticesSaga() {
  yield takeLatest(GET_NOTICES, getNotices);
}

export function* sendNoticeSaga() {
  yield takeLatest(SEND_NOTICE, sendNotice);
}

export function* sentNoticeResponseSaga() {
  yield takeLatest(SEND_NOTICE_RESPONSE, sentNoticeResponse);
}

export function* getMoreNoticesHistorySaga() {
  yield takeLatest(GET_NOTICES_HISTORY, getNoticesHistoryMore);
}

export function* getNoticeHistoryByIdSaga() {
  yield takeLatest(GET_NOTICE_HISTORY_BY_ID, getNoticeHistoryById);
}

export function* getNoticeHistoryDeliveryStatusSaga() {
  yield takeLatest(GET_NOTICE_HISTORY_DELIVERY_STATUS, getNoticeHistoryDeliveryStatus);
}

export function* createNotificationWithFiltersWatcher() {
  yield takeLatest(CREATE_NOTIFICATION_WITH_FILTERS, createNotificationWithFiltersSaga);
}

export function* getNotificationTargetsWatcher() {
  yield takeLatest(GET_NOTIFICATION_TARGETS, getNotificationTargetsSaga);
}

export function* getNotificationFilterOptionsWatcher() {
  yield takeLatest(GET_NOTIFICATION_FILTER_OPTIONS, getNotificationFilterOptionsSaga);
}

export function* getNotificationReceiversWatcher() {
  yield takeLatest(GET_NOTIFICATION_RECEIVERS, getNotificationReceiversSaga);
}

export default function* rootSaga() {
  yield all([
    fork(getNoticeDeliveryStatusSaga),
    fork(getNoticeByIdSaga),
    fork(getUnreadNoticesCountSaga),
    fork(getNoticesSaga),
    fork(sendNoticeSaga),
    fork(sentNoticeResponseSaga),
    fork(getMoreNoticesHistorySaga),
    fork(getNoticeHistoryByIdSaga),
    fork(getNoticeHistoryDeliveryStatusSaga),
    fork(createNotificationWithFiltersWatcher),
    fork(getNotificationTargetsWatcher),
    fork(getNotificationFilterOptionsWatcher),
    fork(getNotificationReceiversWatcher),
  ]);
}
